import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { LogEntry, LogFilters, TimeRangePreset, TIME_RANGE_PRESETS } from '../types/shard'
import { LiveLogModal } from './LiveLogModal'

export interface LogsPanelProps {
  logs: LogEntry[]
  isOpen: boolean
  onToggle: () => void
  /**
   * When provided, log lines become clickable to drill into the full agent
   * response (`.starforge/logs/live/`) on demand. Omitted in local/tests where
   * the live-log endpoints aren't wired.
   */
  getAuthHeaders?: () => Record<string, string>
}

interface LiveLogTarget {
  persona?: string
  worker?: string
  taskId?: string
  label: string
}

// Pull a task id out of a log message, e.g. "DONE task TASK-007 (...)".
function extractTaskId(message: string): string | undefined {
  const m = message.match(/((?:TASK|SPEC)-\d+)/)
  return m ? m[1] : undefined
}

const MIN_WIDTH = 250
const MAX_WIDTH_PERCENT = 0.6
const DEFAULT_WIDTH = 420

export function LogsPanel({ logs, isOpen, onToggle, getAuthHeaders }: LogsPanelProps) {
  const [liveLogTarget, setLiveLogTarget] = useState<LiveLogTarget | null>(null)
  const drillEnabled = typeof getAuthHeaders === 'function'
  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = localStorage.getItem('starforge-logs-width')
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH
  })
  const [filters, setFilters] = useState<LogFilters>({
    workers: [],
    eventTypes: [],
    timeRange: { start: null, end: null },
    search: '',
  })
  const [timePreset, setTimePreset] = useState<TimeRangePreset>('all')
  const [autoScroll, setAutoScroll] = useState(true)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const isDragging = useRef(false)

  // Persist panel width
  useEffect(() => {
    localStorage.setItem('starforge-logs-width', String(panelWidth))
  }, [panelWidth])

  // Drag handle for resizing
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    isDragging.current = true
    const startX = e.clientX
    const startWidth = panelWidth

    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return
      const delta = startX - e.clientX
      const maxWidth = window.innerWidth * MAX_WIDTH_PERCENT
      const newWidth = Math.min(maxWidth, Math.max(MIN_WIDTH, startWidth + delta))
      setPanelWidth(newWidth)
    }

    const onMouseUp = () => {
      isDragging.current = false
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }, [panelWidth])

  // Extract unique personas (for filtering) and event types from logs
  const uniquePersonas = useMemo(() => {
    const personas = new Set<string>()
    logs.forEach(log => {
      if (log.persona) personas.add(log.persona)
      else if (log.worker) personas.add(log.worker)
    })
    return Array.from(personas).sort()
  }, [logs])

  const uniqueEventTypes = useMemo(() => {
    const types = new Set<string>()
    logs.forEach(log => {
      if (log.eventType) types.add(log.eventType)
    })
    return Array.from(types).sort()
  }, [logs])

  // Filter logs
  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      if (filters.workers.length > 0) {
        const logPersona = log.persona || log.worker
        if (!logPersona || !filters.workers.includes(logPersona)) return false
      }
      if (filters.eventTypes.length > 0 && log.eventType && !filters.eventTypes.includes(log.eventType)) {
        return false
      }
      if (filters.timeRange.start || filters.timeRange.end) {
        const logTime = new Date(log.timestamp)
        if (filters.timeRange.start && logTime < filters.timeRange.start) return false
        if (filters.timeRange.end && logTime > filters.timeRange.end) return false
      }
      if (filters.search && !log.message.toLowerCase().includes(filters.search.toLowerCase())) {
        return false
      }
      return true
    })
  }, [logs, filters])

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && scrollContainerRef.current && isOpen) {
      if (typeof scrollContainerRef.current.scrollTo === 'function') {
        scrollContainerRef.current.scrollTo({
          top: scrollContainerRef.current.scrollHeight,
          behavior: 'smooth'
        })
      }
    }
  }, [filteredLogs, autoScroll, isOpen])

  const handleTimePresetChange = (preset: TimeRangePreset) => {
    setTimePreset(preset)
    const range = TIME_RANGE_PRESETS[preset].getRange()
    setFilters(prev => ({ ...prev, timeRange: range }))
  }

  const formatTimestamp = (timestamp: string) => {
    if (!timestamp || timestamp === 'unknown') return ''
    const date = new Date(timestamp)
    if (!isNaN(date.getTime())) return date.toLocaleTimeString()
    const withZ = new Date(timestamp + 'Z')
    if (!isNaN(withZ.getTime())) return withZ.toLocaleTimeString()
    return timestamp
  }

  const getEventTypeColor = (eventType?: string) => {
    if (!eventType) return 'text-gray-300'
    const type = eventType.toUpperCase()
    if (type === 'BLOCKED') return 'text-cosmic-plasma'
    if (type === 'ERROR' || type === 'VERIFY_FAIL') return 'text-red-400'
    if (type === 'WARN' || type === 'WARNING') return 'text-yellow-400'
    if (type === 'DONE' || type === 'VERIFIED') return 'text-green-400'
    if (type === 'START' || type === 'SPAWN') return 'text-blue-400'
    if (type === 'SCAN') return 'text-cyan-400'
    if (type === 'INTERVENE') return 'text-orange-400'
    if (type === 'DEBUG') return 'text-gray-500'
    return 'text-gray-300'
  }

  if (!isOpen) return null

  return (
    <div
      data-testid="logs-panel"
      className="flex-shrink-0 h-full flex border-l border-gray-800"
      style={{ width: panelWidth }}
    >
      {/* Drag handle */}
      <div
        onMouseDown={handleMouseDown}
        className="w-1.5 cursor-col-resize hover:bg-cosmic-nebula/60 active:bg-cosmic-nebula bg-gray-700/50 transition-colors flex-shrink-0 group relative"
      >
        <div className="absolute inset-y-0 -left-1 -right-1" />
      </div>

      {/* Panel content */}
      <div className="flex-1 flex flex-col min-w-0 bg-gray-900">
        {/* Header with controls */}
        <div className="flex-shrink-0 border-b border-gray-800 p-2 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-300">
              Logs ({filteredLogs.length}/{logs.length})
            </span>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={autoScroll}
                  onChange={(e) => setAutoScroll(e.target.checked)}
                  className="rounded w-3 h-3"
                  aria-label="Auto-scroll"
                />
                <span className="text-xs text-gray-400">Auto</span>
              </label>
              <button
                onClick={onToggle}
                className="text-gray-500 hover:text-gray-300 text-xs px-1"
                aria-label="Close logs"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Filters row */}
          <div className="flex flex-wrap gap-1">
            <input
              type="text"
              placeholder="Search..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="flex-1 min-w-[100px] px-2 py-1 text-xs bg-gray-800 border border-gray-700 rounded text-gray-300 placeholder-gray-500 focus:outline-none focus:border-gray-600"
            />
            <select
              value={timePreset}
              onChange={(e) => handleTimePresetChange(e.target.value as TimeRangePreset)}
              aria-label="Filter by time"
              className="px-1 py-1 text-xs bg-gray-800 border border-gray-700 rounded text-gray-300 focus:outline-none focus:border-gray-600"
            >
              {Object.entries(TIME_RANGE_PRESETS).map(([key, { label }]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            <select
              value={filters.workers[0] || ''}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                workers: e.target.value ? [e.target.value] : []
              }))}
              aria-label="Filter by worker"
              className="px-1 py-1 text-xs bg-gray-800 border border-gray-700 rounded text-gray-300 focus:outline-none focus:border-gray-600"
            >
              <option value="">All Personas</option>
              {uniquePersonas.map(persona => (
                <option key={persona} value={persona}>{persona}</option>
              ))}
            </select>
            <select
              value={filters.eventTypes[0] || ''}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                eventTypes: e.target.value ? [e.target.value] : []
              }))}
              aria-label="Filter by event type"
              className="px-1 py-1 text-xs bg-gray-800 border border-gray-700 rounded text-gray-300 focus:outline-none focus:border-gray-600"
            >
              <option value="">All Events</option>
              {uniqueEventTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Log content */}
        <div
          ref={scrollContainerRef}
          data-testid="logs-container"
          className="flex-1 overflow-y-auto p-3 bg-black min-h-0"
        >
          {filteredLogs.length === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-500 text-sm">
              {logs.length === 0 ? 'Waiting for activity...' : 'No logs match current filters'}
            </div>
          ) : (
            <div className="space-y-0.5">
              {filteredLogs.map(log => (
                <div
                  key={log.id}
                  data-testid={`log-line-${log.id}`}
                  onClick={drillEnabled ? () => setLiveLogTarget({
                    persona: log.persona,
                    worker: log.worker,
                    taskId: extractTaskId(log.message),
                    label: log.message,
                  }) : undefined}
                  title={drillEnabled ? 'Click to view the full agent response' : undefined}
                  className={`font-mono text-xs leading-relaxed ${getEventTypeColor(log.eventType)} ${drillEnabled ? 'cursor-pointer hover:bg-gray-800/60 rounded px-1 -mx-1' : ''}`}
                >
                  {formatTimestamp(log.timestamp) && (
                    <>
                      <span className="text-gray-600">{formatTimestamp(log.timestamp)}</span>
                      {' '}
                    </>
                  )}
                  {(log.persona || log.worker) && (
                    <>
                      <span className="text-blue-400">
                        [{log.persona && log.worker && log.persona !== log.worker
                          ? `${log.persona}/${log.worker}`
                          : log.persona || log.worker}]
                      </span>
                      {' '}
                    </>
                  )}
                  {log.eventType && (
                    <>
                      <span className="uppercase text-gray-500">{log.eventType}</span>
                      {' '}
                    </>
                  )}
                  {log.message}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {drillEnabled && (
        <LiveLogModal
          target={liveLogTarget}
          onClose={() => setLiveLogTarget(null)}
          getAuthHeaders={getAuthHeaders!}
        />
      )}
    </div>
  )
}
